import { createLogger } from './logger';

// Define KV namespace interface
interface KVNamespace {
  get(key: string, type?: 'text' | 'json' | 'arrayBuffer' | 'stream'): Promise<unknown>;
  put(key: string, value: string | ArrayBuffer | ReadableStream, options?: { expirationTtl?: number }): Promise<void>;
  delete(key: string): Promise<void>;
  list(options?: { prefix?: string }): Promise<{ keys: Array<{ name: string }> }>;
}

// Declare global KV binding for Cloudflare Pages
declare global {
  const KV: KVNamespace | undefined;
  const CAREER_CARD_CACHE: KVNamespace | undefined;
}

const logger = createLogger({ module: 'CloudflareKVCache', level: 'warn' });

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  userId: string;
  ttl: number;
  version: string;
  dependencies?: string[];
}

interface CacheOptions {
  ttl?: number;
  dependencies?: string[];
  version?: string;
}

interface CacheResult<T> {
  data: T | null;
  status: 'valid' | 'expired' | 'not_found' | 'stale_offline' | 'stale_fallback' | 'fresh';
  source: 'kv_cache' | 'miss' | 'offline_cache' | 'fallback_cache' | 'network';
  age?: number;
}

export class CloudflareKVCache {
  private readonly KV_NAMESPACE = 'CAREER_CARD_CACHE';
  private isKVAvailable: boolean;
  private kvNamespace: KVNamespace | null;
  private isBrowserEnvironment: boolean;
  
  constructor() {
    // Detect environment first
    this.isBrowserEnvironment = typeof window !== 'undefined';
    
    // KV bindings are only available in server-side contexts (Workers, edge functions, SSR)
    // They are NOT available in client-side browser code
    if (this.isBrowserEnvironment) {
      this.isKVAvailable = false;
      this.kvNamespace = null;
      
      logger.debug('Browser environment detected - KV bindings not available (this is expected)');
      return;
    }
    
    // Only attempt to access KV bindings in server-side contexts
    let kvNamespace = null;
    
    try {
      // Method 1: Direct global CAREER_CARD_CACHE (your binding name)
      if (typeof CAREER_CARD_CACHE !== 'undefined') {
        kvNamespace = CAREER_CARD_CACHE;
        logger.debug('Found KV via direct CAREER_CARD_CACHE global');
      }
      // Method 2: Direct global KV
      else if (typeof KV !== 'undefined') {
        kvNamespace = KV;
        logger.debug('Found KV via direct global');
      }
      // Method 3: globalThis.CAREER_CARD_CACHE
      else if (typeof globalThis !== 'undefined' && (globalThis as typeof globalThis & { CAREER_CARD_CACHE?: KVNamespace }).CAREER_CARD_CACHE) {
        kvNamespace = (globalThis as typeof globalThis & { CAREER_CARD_CACHE?: KVNamespace }).CAREER_CARD_CACHE;
        logger.debug('Found KV via globalThis.CAREER_CARD_CACHE');
      }
      // Method 4: globalThis.KV
      else if (typeof globalThis !== 'undefined' && (globalThis as typeof globalThis & { KV?: KVNamespace }).KV) {
        kvNamespace = (globalThis as typeof globalThis & { KV?: KVNamespace }).KV;
        logger.debug('Found KV via globalThis.KV');
      }
    } catch (error) {
      logger.error('Error detecting KV namespace:', error);
    }
    
    this.kvNamespace = kvNamespace ?? null;
    this.isKVAvailable = !!this.kvNamespace;
    
    logger.debug('KV Cache initialization:', {
      environment: this.isBrowserEnvironment ? 'browser' : 'server',
      isKVAvailable: this.isKVAvailable,
      kvNamespace: !!this.kvNamespace,
      note: this.isBrowserEnvironment ? 'KV not available in browser (expected)' : 'Server-side environment'
    });
    
    if (!this.isKVAvailable && !this.isBrowserEnvironment) {
      logger.warn('Cloudflare KV not available in server environment - check binding configuration');
    } else if (this.isKVAvailable) {
      logger.debug('Cloudflare KV cache initialized successfully');
    }
  }

  async get<T>(key: string, userId: string): Promise<CacheResult<T>> {
    if (!this.isKVAvailable) {
      return {
        data: null,
        status: 'not_found',
        source: 'miss'
      };
    }

    try {
      const kvKey = this.buildKey(key, userId);
      const entry = await this.kvNamespace!.get(kvKey, 'json') as CacheEntry<T> | null;
      
      if (!entry || entry.userId !== userId) {
        return {
          data: null,
          status: 'not_found',
          source: 'miss'
        };
      }
      
      const now = Date.now();
      const age = now - entry.timestamp;
      
      // Check TTL expiration
      if (age > entry.ttl) {
        // Clean up expired entry
        await this.delete(key, userId);
        return {
          data: null,
          status: 'expired',
          source: 'miss',
          age
        };
      }
      
      logger.debug('Cache hit', { key, userId: userId.substring(0, 8), age });
      
      return {
        data: entry.data,
        status: 'valid',
        source: 'kv_cache',
        age
      };
    } catch (error) {
      logger.error('KV cache read failed:', { key, error: error instanceof Error ? error.message : String(error) });
      return {
        data: null,
        status: 'not_found',
        source: 'miss'
      };
    }
  }
  
  async set<T>(
    key: string, 
    data: T, 
    userId: string, 
    options: CacheOptions = {}
  ): Promise<void> {
    if (!this.isKVAvailable) {
      logger.debug('KV not available, skipping cache set', { key, environment: this.isBrowserEnvironment ? 'browser' : 'server' });
      return;
    }

    const {
      ttl = 300000, // 5 minutes default
      dependencies = [],
      version = '1.0'
    } = options;
    
    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      userId,
      ttl,
      version,
      dependencies
    };
    
    try {
      const kvKey = this.buildKey(key, userId);
      
      await this.kvNamespace!.put(
        kvKey,
        JSON.stringify(entry),
        {
          expirationTtl: Math.floor(ttl / 1000) // Convert to seconds
        }
      );
      
      logger.debug('Cache set successful', { 
        key, 
        userId: userId.substring(0, 8), 
        ttl,
        dataSize: JSON.stringify(data).length 
      });
    } catch (error) {
      logger.error('KV cache write failed:', {
        key,
        error: error instanceof Error ? error.message : String(error)
      });
    }
  }
  
  async invalidate(pattern: string, userId: string): Promise<void> {
    if (!this.isKVAvailable) {
      logger.debug('KV not available, skipping cache invalidation', { pattern });
      return;
    }

    try {
      // For pattern invalidation, we need to find dependent keys
      // Since KV doesn't support pattern deletion directly, we'll use metadata
      const dependentKeys = await this.findDependentKeys(pattern, userId);
      
      await Promise.allSettled(
        dependentKeys.map(key => this.delete(key, userId))
      );
      
      logger.debug('Cache invalidation completed', { 
        pattern, 
        userId: userId.substring(0, 8), 
        keysInvalidated: dependentKeys.length 
      });
    } catch (error) {
      logger.error('Cache invalidation failed:', {
        pattern,
        error: error instanceof Error ? error.message : String(error)
      });
    }
  }
  
  async clear(userId: string): Promise<void> {
    if (!this.isKVAvailable) {
      logger.debug('KV not available, skipping cache clear');
      return;
    }

    try {
      // List all keys for this user and delete them
      const userKeys = await this.listUserKeys(userId);
      
      await Promise.allSettled(
        userKeys.map(key => this.kvNamespace!.delete(key))
      );
      
      console.log('User cache cleared', { 
        userId: userId.substring(0, 8), 
        keysCleared: userKeys.length 
      });
    } catch (error) {
      logger.error('Cache clear failed:', {
        userId: userId.substring(0, 8),
        error: error instanceof Error ? error.message : String(error)
      });
    }
  }
  
  async getStaleData<T>(key: string, userId: string): Promise<T | null> {
    if (!this.isKVAvailable) {
      return null;
    }

    try {
      const kvKey = this.buildKey(key, userId);
      const entry = await this.kvNamespace!.get(kvKey, 'json') as CacheEntry<T> | null;
      
      if (!entry || entry.userId !== userId) {
        return null;
      }
      
      // Return data even if expired (for offline scenarios)
      logger.debug('Stale cache data retrieved', { 
        key, 
        userId: userId.substring(0, 8),
        age: Date.now() - entry.timestamp 
      });
      
      return entry.data;
    } catch (error) {
      logger.error('Stale cache read failed:', {
        key,
        error: error instanceof Error ? error.message : String(error)
      });
      return null;
    }
  }
  
  private buildKey(key: string, userId: string): string {
    return `user:${userId}:${key}`;
  }
  
  private async findDependentKeys(pattern: string, userId: string): Promise<string[]> {
    if (!this.isKVAvailable) {
      return [];
    }

    try {
      // Use KV list operation with prefix to find matching keys
      const prefix = `user:${userId}:`;
      const listResult = await this.kvNamespace!.list({ prefix });
      
      const dependentKeys: string[] = [];
      
      for (const key of listResult.keys) {
        // Check if key matches pattern or has dependencies that match
        if (key.name.includes(pattern)) {
          dependentKeys.push(key.name);
        } else {
          // Note: KV metadata is stored in the entry itself, not in list metadata
          // Skip dependency checking since we removed metadata from put options
        }
      }
      
      return dependentKeys;
    } catch (error) {
      logger.error('Failed to find dependent keys:', {
        pattern,
        error: error instanceof Error ? error.message : String(error)
      });
      return [];
    }
  }
  
  private async listUserKeys(userId: string): Promise<string[]> {
    if (!this.isKVAvailable) {
      return [];
    }

    try {
      const prefix = `user:${userId}:`;
      const listResult = await this.kvNamespace!.list({ prefix });
      
      return listResult.keys.map((key) => key.name);
    } catch (error) {
      logger.error('Failed to list user keys:', {
        userId: userId.substring(0, 8),
        error: error instanceof Error ? error.message : String(error)
      });
      return [];
    }
  }
  
  private async delete(key: string, userId: string): Promise<void> {
    if (!this.isKVAvailable) {
      return;
    }

    try {
      const kvKey = this.buildKey(key, userId);
      await this.kvNamespace!.delete(kvKey);
      
      logger.debug('Cache key deleted', { key, userId: userId.substring(0, 8) });
    } catch (error) {
      logger.error('Cache delete failed:', {
        key,
        error: error instanceof Error ? error.message : String(error)
      });
    }
  }
  
  // Utility methods for cache management
  async getCacheStats(userId: string): Promise<{
    totalKeys: number;
    totalSize: number;
    oldestEntry: number | null;
    newestEntry: number | null;
  }> {
    if (!this.isKVAvailable) {
      return {
        totalKeys: 0,
        totalSize: 0,
        oldestEntry: null,
        newestEntry: null
      };
    }

    try {
      const userKeys = await this.listUserKeys(userId);
      let totalSize = 0;
      let oldestEntry: number | null = null;
      let newestEntry: number | null = null;
      
      for (const key of userKeys) {
        try {
          const entry = await this.kvNamespace!.get(key, 'json') as { timestamp?: number } | null;
          if (entry) {
            const entrySize = JSON.stringify(entry).length;
            totalSize += entrySize;

            if (entry.timestamp) {
              if (!oldestEntry || entry.timestamp < oldestEntry) {
                oldestEntry = entry.timestamp;
              }
              if (!newestEntry || entry.timestamp > newestEntry) {
                newestEntry = entry.timestamp;
              }
            }
          }
        } catch (error) {
          // Skip individual key errors
          logger.debug('Error reading cache entry for stats:', { key, error });
        }
      }
      
      return {
        totalKeys: userKeys.length,
        totalSize,
        oldestEntry,
        newestEntry
      };
    } catch (error) {
      logger.error('Failed to get cache stats:', {
        userId: userId.substring(0, 8),
        error: error instanceof Error ? error.message : String(error)
      });
      return {
        totalKeys: 0,
        totalSize: 0,
        oldestEntry: null,
        newestEntry: null
      };
    }
  }
  
  // Health check method
  async healthCheck(): Promise<{
    available: boolean;
    latency?: number;
    error?: string;
  }> {
    if (!this.isBrowserEnvironment) {
      return {
        available: false,
        error: 'KV cache only available in browser environment'
      };
    }

    if (!this.isKVAvailable) {
      return {
        available: false,
        error: 'KV namespace not available'
      };
    }

    const startTime = performance.now();
    
    try {
      // Test with a simple operation
      const testKey = `health_check_${Date.now()}`;
      const testData = { test: true, timestamp: Date.now() };
      
      await this.kvNamespace!.put(testKey, JSON.stringify(testData));
      const retrieved = await this.kvNamespace!.get(testKey);
      await this.kvNamespace!.delete(testKey);
      
      const latency = performance.now() - startTime;
      
      if (retrieved) {
        return {
          available: true,
          latency: Math.round(latency)
        };
      } else {
        return {
          available: false,
          error: 'KV operations failed - data not retrieved'
        };
      }
    } catch (error) {
      const latency = performance.now() - startTime;
      return {
        available: false,
        latency: Math.round(latency),
        error: error instanceof Error ? error.message : 'Unknown KV error'
      };
    }
  }
  
  // Check if we're in a browser environment
  isBrowser(): boolean {
    return this.isBrowserEnvironment;
  }
  
  // Check if KV is available (will be false in browser)
  isAvailable(): boolean {
    return this.isKVAvailable;
  }
}

// Export a singleton instance
export const cloudflareKVCache = new CloudflareKVCache(); 